import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '__generated__/globalTypes';
import { Vertical } from 'atoms/layout/flex';
import { BrandS, LabelS } from 'atoms/typography';
import {
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET_COMMON_PACKS,
} from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import { usePinnedShardRarity } from 'hooks/config/usePinnedShardRarity';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { isSpecialCardSet, isSupportedCardSet } from 'lib/cardSets';
import { formatScarcity } from 'lib/cards';
import { getRarityColor } from 'lib/colors';
import { laptopAndAbove } from 'style/mediaQuery';

import { PinButton } from '../PinButton';

const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit);
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;

const RarityContainer = styled(Vertical).attrs({ center: true })`
  width: 100%;
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

type Props = {
  rarity: Rarity;
  cardShardsCount: number;
  cardSet: Nullable<{
    slug: string;
    name: string;
  }>;
  onClick?: () => void;
};

export const ShardRarityItem = ({
  rarity,
  cardShardsCount,
  cardSet,
  onClick,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLaptop = useIsLaptop();
  const { pinnedShardRarity, pinnedShardCardSet, updatePinnedShardRarity } =
    usePinnedShardRarity();

  const isPinned =
    pinnedShardRarity === rarity &&
    ((!pinnedShardCardSet && !cardSet?.slug) ||
      pinnedShardCardSet === cardSet?.slug);

  const cardSetSlug = isSupportedCardSet(cardSet?.slug)
    ? cardSet?.slug
    : undefined;
  return (
    <RarityContainer>
      {!!isLaptop && (
        <PinButton
          onClick={() => updatePinnedShardRarity(rarity, cardSetSlug)}
          isPinned={isPinned}
        />
      )}
      <RarityWrapper
        as={Link}
        to={
          rarity === Rarity.common
            ? generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
            : generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
                params: { type: rarity },
              })
        }
        onClick={onClick}
      >
        <LabelS
          color={
            isSpecialCardSet(cardSet?.slug)
              ? getRarityColor(rarity, { cardSet: cardSetSlug })
              : undefined
          }
        >
          {isSpecialCardSet(cardSet?.slug)
            ? cardSet?.name
            : formatScarcity(rarity)}
        </LabelS>
        <BrandS>
          <FormattedNumber value={cardShardsCount} />
        </BrandS>
      </RarityWrapper>
      {!isLaptop && (
        <PinButton
          onClick={() => updatePinnedShardRarity(rarity, cardSetSlug)}
          isPinned={isPinned}
        />
      )}
    </RarityContainer>
  );
};
